import PropTypes from 'prop-types';
import React, { memo } from 'react';
import { classNames } from '../../../utils/classnames';

/**
 * @description Render memoized product color swatch component
 * @param {Array} colors
 * @param {number} selected
 * @param {Function} handleChange
 * @param {String} className
 * @returns {JSX.Element} Memoized product color swatch component
 */
export const ProductColorSwatch = memo(function MemoizedProductColorSwatch(props) {
  let { colors, selected, handleChange, className, options, product, selectedOption } = props;

  const colorPickerId =
    options && options?.length
      ? options.filter((option) => option?.name?.toLowerCase() === 'color').map((el) => el.option_id)[0]
      : null;
  function filterObjectsByValueForOptionId(arr, valueSelected) {
    return arr.filter((obj) => valueSelected.every((id) => obj.option_values.some((optVal) => optVal.id === id)));
  }

  function findDisabledColorSwatches() {
    //get list of all variants.
    const allVariants = product?.variants;

    // get optionIds of all selected options except color swatches
    const selectedOptionOptionIds =
      selectedOption && selectedOption.length
        ? selectedOption.filter((el) => el.option_id !== colorPickerId).map((item) => item.option_id)
        : [];

    // get valudIds of all selected options except color swatches
    const selectedOptionValueIds =
      selectedOption && selectedOption.length
        ? selectedOption.filter((el) => el.option_id !== colorPickerId).map((item) => item.id)
        : [];
    const availableOptionsExceptColorSwatch =
      options && options.length
        ? options.filter((option) => option?.name?.toLowerCase() !== 'color').map((el) => el.option_id)
        : null;
    const areAllDropDownsSelected =
      availableOptionsExceptColorSwatch &&
      availableOptionsExceptColorSwatch.every((optionId) => selectedOptionOptionIds.includes(optionId));

    // add condition check to only execute below code when all filters are selected except color swatches
    if (areAllDropDownsSelected) {
      const availableColorsAsPerDropDownFilters = filterObjectsByValueForOptionId(allVariants, selectedOptionValueIds);
      // filter out variants from main array as value seleced in 1st filter
      // filter out variants from filtered array value selected in 2nd filter
      // at the end of all filters there should be remaining color swatches available variants as per current filter combination.
      // check for inventory_level 0 in those and you got the feature completed

      const findIdInOptionValues = (obj, targetId) =>
        obj.find((item) => item.option_values && item.option_values.some((value) => value.id === targetId));
      colors = colors.map((color) => {
        return {
          ...color,
          is_out_of_stock: findIdInOptionValues(availableColorsAsPerDropDownFilters, color.id)?.inventory_level === 0
        };
      });
    }
  }
  findDisabledColorSwatches();
  return colors && Array.isArray(colors) && colors?.length ? (
    <div className={classNames('mb-2 mt-1 flex max-w-[132px] flex-row flex-wrap gap-2', className)}>
      <>
        {colors?.map((item, index) => {
          return (
            item?.value_data && (
              <button disabled={item?.is_out_of_stock} key={index} onClick={() => handleChange(item?.id)}>
                <div
                  className={classNames(
                    'circle-color-swatch h-5 w-5 rounded-full border',
                    item?.is_out_of_stock ? 'out-of-stock' : '',
                    selected === item?.id ? 'border border-[#707070]' : 'border-[#e5e5e5]]'
                  )}
                  style={item?.value_data?.colors && { backgroundColor: item?.value_data?.colors?.[0] }}
                >
                  {item?.value_data?.image_url ? (
                    <img src={item.value_data.image_url} alt="option" className="w-full rounded-full" loading="lazy" />
                  ) : null}
                </div>
              </button>
            )
          );
        })}
      </>
    </div>
  ) : null;
});

ProductColorSwatch.propTypes = {
  className: PropTypes.string,
  colors: PropTypes.array,
  handleChange: PropTypes.func,
  selected: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};
